/**
 * 拡張属性 (AI が UI より広い値空間で書ける属性) の宣言。
 * docs/features/video-editor-capability-gaps-plan.md §0 / §5-0 の共通基盤。
 *
 * §0 の方針:
 * 1. UI で微調整できなくてよい (AI の値空間 ⊇ UI の値空間)
 * 2. Premiere / FCPXML に完全再現できなくてよい (対応物が無ければ落として書き出し時に注記)
 * 3. AI が書いた属性は UI 編集・AI 再編集の両方で**保持**し、**競合するときだけ**リセットする
 *
 * この表は「どの属性が、どの操作でどう扱われるか」を 1 か所に宣言するためのもので、
 * UI のフォーム (SceneInspector 等) と提案の引き継ぎ (proposal/itemSettingsContinuity.ts) が
 * 実装で従う規約の正本。**大がかりな汎用エンジンは持たない** (宣言と実装の対応は目視で保つ)。
 *
 * 消費者 (この宣言を実際に読む実装):
 * - proposal/itemSettingsContinuity.ts の restoreItemSettingsContinuity (省略 = 変更なしの引き継ぎと、
 *   明示値の正規化をこの表から引く。属性名をハードコードしない)
 * - proposal/captionContinuity.ts の carryCaptionDesignExtensions (AI が design を書き換えたとき、
 *   省略された拡張属性を引き継ぎ、競合する属性 (position) を変えたときだけ捨てる)
 * - components/CaptionEditor.tsx の handleDesignChange (UI のフォームが applyExtensionConflicts を通す)
 * - schema/__tests__/extensionAttributes.test.ts (宣言した key が実際のスキーマに在ることを検証)
 *
 * 対応する backend 側の保持経路:
 * - propose_service.normalize_items / normalize_caption_design (AI の全体提案を正規化する際に落とさない)
 * - proposal_continuity.restore_item_settings_continuity / restore_caption_continuity
 *   (AI が省略 = 変更なし → 既存値を保持)
 * - edit_ops (set_item_* / split_item / set_item_range / set_item_speed / set_caption /
 *   apply_caption_design が落とさない)
 */

import { normalizeCaptionAnimation } from './captionAnimation';
import { normalizeCaptionEmphasisStyle, normalizeEmphasisRanges } from './captionEmphasis';
import { normalizeCaptionRotationValue } from './captionRotation';
import { normalizeCaptionWordHighlight } from './captionWordHighlight';
import { normalizeCaptionGradient } from './captionGradient';
import { normalizeCaptionLetterSpacingEmValue } from './project';
import { normalizeItemBackgroundColor } from './backgroundColor';
import { normalizeCrop } from './crop';
import { normalizePlacement } from './placement';
import { normalizeEffects } from './effects';
import { normalizeFreeze } from './freeze';
import { normalizeItemLook } from './lookCatalog';
import { normalizeSpeedRamp } from './speedRamp';
import { normalizeLoudness } from '../audio/loudness';
import { normalizeMotion, type Motion } from './motion';
import { normalizeOverlays } from './overlay';
import { normalizeDucking } from '../audio/ducking';
import { normalizeAudioRegions } from '../audio/audioRegions';

/**
 * 拡張属性が乗るオブジェクト。
 * - `timelineItem`: TimelineItem (ズーム / パン・クロップ・シーン単位の色補正・フリーズ / 速度ランプ・
 *   シーン音声のフェード・シーンのオーバーレイ・映像エフェクト)
 * - `captionDesign`: CaptionDesign (テロップの位置補正・行間・アニメーション)
 * - `caption`: Caption 本体 (部分強調の範囲 emphasisRanges・単語ハイライトの対応づけ wordTimings)
 * - `narrationCue`: NarrationCue (ナレーション 1 本ごとの音量・フェード・鳴らす長さ・入り点)
 * - `project`: プロジェクト直下 (ナレーション中の BGM 自動ダッキング・書き出し時のラウドネス正規化・
 *   全編に効くオーバーレイ)
 */
export type ExtensionAttributeOwner =
  | 'timelineItem'
  | 'caption'
  | 'captionDesign'
  | 'narrationCue'
  | 'project';

export interface ExtensionAttributeDeclaration {
  /** 属性のキー (owner のオブジェクト上のプロパティ名)。 */
  key: string;
  owner: ExtensionAttributeOwner;
  /** 人間向けの説明 (この表を読む人のため)。 */
  description: string;
  /**
   * 同じ意味の値を持つ属性 (UI でこちらを変えたら拡張属性をリセットする)。
   * 空配列 = 競合なし (UI の他の操作では常に保持する)。
   */
  conflictsWith: string[];
  /**
   * 前提にしている属性 (これが変わったら拡張属性を破棄して再生成を促す)。
   * 空配列 = 依存なし。
   */
  dependsOn: string[];
  /**
   * シーン分割 (split_item) での扱い。
   * - `window`: 元曲線の評価区間を切り分ける (motion。§4.1-A)
   * - `split`: 分割点で属性そのものを切り分ける (freeze の hold / speedRamp の速度。§4.1-L)
   * - `copy`: 前半・後半へそのまま複製する
   * - `drop`: 破棄する
   */
  onSplit: 'window' | 'split' | 'copy' | 'drop';
  /** 区間変更 (set_item_range) / 速度変更 (set_item_speed) での扱い。 */
  onRangeOrSpeedChange: 'keep' | 'drop';
  /** Premiere / FCPXML 書き出しでの扱い (§0-2)。 */
  onXmlExport: 'omit-with-note' | 'approximate';
  /**
   * 外部入力 (AI が明示した値) を保存形へ正規化する関数。
   * `undefined` / `null` を返したら「属性なし」としてキーごと落とす。
   * 宣言を省略した場合は値をそのまま採用する (この場合も `undefined` / `null` のときだけ削除し、
   * 0 や '' や false は有効な値として保持する)。
   */
  normalize?: (value: unknown) => unknown;
}

export const EXTENSION_ATTRIBUTES: ExtensionAttributeDeclaration[] = [
  {
    key: 'hollow', owner: 'captionDesign', description: '文字の塗りを透明にし輪郭だけを描く',
    conflictsWith: [], dependsOn: [], onSplit: 'copy', onRangeOrSpeedChange: 'keep',
    onXmlExport: 'omit-with-note', normalize: (value) => typeof value === 'boolean' ? value : undefined,
  },
  {
    key: 'italic', owner: 'captionDesign', description: '文字を斜体 (合成の斜体) にする',
    conflictsWith: [], dependsOn: [], onSplit: 'copy', onRangeOrSpeedChange: 'keep',
    // Final Cut (text-style の italic) / Premiere (Text ジェネレータの Style) にそのまま渡せる
    onXmlExport: 'approximate', normalize: (value) => typeof value === 'boolean' ? value : undefined,
  },
  {
    key: 'gradient', owner: 'captionDesign', description: '文字のグラデーション (2 色目と向き。1 色目は color)',
    conflictsWith: [], dependsOn: [], onSplit: 'copy', onRangeOrSpeedChange: 'keep',
    // Premiere / Final Cut のテキストにはグラデーション塗りが無いので落とし、color の単色で出す
    onXmlExport: 'omit-with-note', normalize: normalizeCaptionGradient,
  },
  {
    key: 'letterSpacingEm', owner: 'captionDesign', description: '字間 (em。負で詰める)。単語ハイライトの字間とは加算',
    conflictsWith: [], dependsOn: [], onSplit: 'copy', onRangeOrSpeedChange: 'keep',
    // Premiere / Final Cut のトラッキングに近いが書き出しでは落とす (PNG トラックへの反映もしない)
    onXmlExport: 'omit-with-note', normalize: normalizeCaptionLetterSpacingEmValue,
  },
  {
    key: 'motion',
    owner: 'timelineItem',
    description:
      'ズーム / パン (Ken Burns)。UI は「動き」プリセットと倍率 / 区間 (表示秒) / イージングを触れ、'
      + 'AI は任意の from / to も書ける',
    // 同じ意味の値を持つ UI 項目は無い (UI の「動き」セレクタ / カード自体が motion を書く)。
    conflictsWith: [],
    // 進行 0〜1 の相対指定 (区間は表示秒で、他の区間付き属性と同じ追従規則) なので、依存しない。
    dependsOn: [],
    // 分割は from / to / easing を変えず、元曲線の評価区間 window を切り分ける (§4.1-A)。
    // 区間 (startSec / endSec) を持つときは、区間の前 / 中 / 後の 3 分岐 (schema/motion.ts splitMotion)。
    onSplit: 'window',
    onRangeOrSpeedChange: 'keep',
    // Premiere / FCPXML には出さず、ZIP の README とトーストで「含まれません」と注記する。
    onXmlExport: 'omit-with-note',
    // クランプ後に静止する指定 (実質「動きなし」) は undefined に倒す (schema/motion.ts)。
    normalize: (value) => normalizeMotion(value as Motion | undefined | null),
  },
  // ── シーンのオーバーレイ (§4.1-F / §4.1-G / §4.1-V) ──
  // 画像 / ぼかし / モザイク / 図形 / テキストの矩形。rect は**出力座標に対する %** なので、区間 (in/out)・速度とは
  // 直交する (テロップの位置と同じ考え方)。区間を持つオーバーレイだけがトリム / 分割 / 速度に追従し、
  // その規則はテロップと同じ (schema/overlay.ts の clampOverlaysToDuration / splitOverlays /
  // rescaleOverlaysForSpeed)。
  {
    key: 'overlays',
    owner: 'timelineItem',
    description:
      'シーンに重ねる画像 / ぼかし / モザイク / 図形 / テキストの矩形。'
      + 'UI は一覧と数値 4 つ・プリセット位置と、焼き込み文字の「隠す / 差し替え」だけを触れる',
    // 同じ意味の値を持つ UI 項目は無い (UI の一覧自体が overlays を書く)。
    conflictsWith: [],
    // rect は出力座標なので inSec / outSec とは直交する (区間を持つものだけが表示秒に追従)。
    dependsOn: [],
    // 分割は表示秒で振り分ける (テロップと同じ 3 分岐)。
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // image は上位トラックの静止画クリップとして出せるが、blur / mosaic / shape / text は
    // 対応物が無いので落として注記する (§4.1-G / §4.1-V)。
    onXmlExport: 'approximate',
    normalize: (value) => {
      const { overlays } = normalizeOverlays(value);
      return overlays.length > 0 ? overlays : undefined;
    },
  },
  // ── 映像エフェクト (§4.1-S) ──
  // 窓は**シーンの表示秒**なので、オーバーレイとまったく同じ追従規則 (トリム / 速度で clamp・
  // 分割で切り分け)。正規化で同じ kind を 1 つに畳み、4 件を超える分は落とす。
  {
    key: 'effects',
    owner: 'timelineItem',
    description:
      'シーンの時間窓に掛ける映像エフェクト (揺れ / ズームパンチ / フラッシュ / グリッチ / 光漏れ / グレイン / VHS)。UI は一覧と種類・強さ・窓だけを触れる',
    // 同じ意味の値を持つ UI 項目は無い (UI のエフェクト欄自体が effects を書く)。
    conflictsWith: [],
    // 窓は表示秒だが、区間を持つものだけが表示尺に追従する (テロップ・オーバーレイと同じ)。
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere / FCPXML には当て先が無いので落として注記する (§4.1-S)。
    onXmlExport: 'omit-with-note',
    normalize: (value) => {
      const { effects } = normalizeEffects(value);
      return effects.length > 0 ? effects : undefined;
    },
  },
  // ── クロップ / リフレーム (§4.1-J) ──
  // 矩形は**元素材のフレームに対する %** なので、区間 (in/out)・速度・分割のどれにも依存しない
  // (motion と同じく相対指定)。分割では前半・後半とも同じ矩形を使う。
  {
    key: 'crop',
    owner: 'timelineItem',
    description:
      '映像のクロップ / リフレーム (元素材に対する矩形 % と収め方)。UI は数値 4 つと fit だけを触れる',
    // 同じ意味の値を持つ UI 項目は無い (UI のクロップ欄自体が crop を書く)。
    conflictsWith: [],
    // 素材に対する % なので区間・速度とは直交する。
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere は Crop エフェクト + Basic Motion、Final Cut は adjust-crop で近似できる (§4.1-J)。
    onXmlExport: 'approximate',
    // クランプ後に「全面 + contain」(何も切り出していない) になる指定は undefined に倒す。
    normalize: (value) => normalizeCrop(value),
  },
  // ── サイズと位置 (縮小 + 配置。docs/features/video-editor-scene-placement-plan.md) ──
  // 枠は**出力に対する %** なので、区間 (in/out)・速度・分割のどれにも依存しない (crop と同じ)。
  {
    key: 'placement',
    owner: 'timelineItem',
    description:
      '映像を縮小して画面のどこに置くか (出力に対する大きさ % と余白の配分 x / y %)。UI は数値 3 つと解除だけを触れる',
    // クロップ (素材のどこを使うか) とは掛かる場所が違い、組み合わせられる。
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere は Basic Motion、Final Cut は adjust-transform で近似する (余白の色は出ない。README で注記)。
    onXmlExport: 'approximate',
    // scale 100 (= 何も縮めていない) は undefined に倒す。
    normalize: (value) => normalizePlacement(value),
  },
  // ── シーン単位の色補正 (§4.1-K) ──
  // プロジェクト全体の look を、このシーンだけ差し替える / 外す。プロジェクト側の look
  // (owner 'project' には宣言していない = 三値マージで扱う) とは別物で、こちらは汎用の引き継ぎに乗る。
  {
    key: 'look',
    owner: 'timelineItem',
    description:
      'シーン単位の色補正 (全体の look の上書き / 解除)。UI は「全体に従う / このシーンだけ外す / プリセット + 強度」',
    // 同じ意味の値を持つ UI 項目は無い (プロジェクト全体の look は別オブジェクト)。
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // LUT はシーンごとに同梱するが、XML 側には当て先が無いので注記で知らせる (§4.1-K)。
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeItemLook(value),
  },
  // ── フリーズフレーム (§4.1-L) ──
  // atSourceSec は**素材秒**なので、区間 (in/out)・速度を変えても止めるコマはずれない
  // (区間の外に出た場合だけ clampFreezeToRange が内側へ寄せる)。速度ランプとは排他。
  {
    key: 'freeze',
    owner: 'timelineItem',
    description:
      'シーンの途中で 1 コマを止める「ため」。UI は位置 (表示秒) と長さ・解除だけを触れる',
    // 速度ランプとは同時に持てない (ランプは表示秒 → 素材秒が非線形で、静止位置を閉じた形で求められない)。
    conflictsWith: ['speedRamp'],
    // 素材秒で持つので区間・速度には依存しない。
    dependsOn: [],
    // 分割点が静止区間の中なら hold を切り分ける (edit_ops._op_split_item)。
    onSplit: 'split',
    onRangeOrSpeedChange: 'keep',
    // Premiere / FCPXML は「前半 + 静止 + 後半」の 3 クリップに分けて近似する (§4.1-L)。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeFreeze(value),
  },
  // ── 速度ランプ (§4.1-L) ──
  // from → to へ連続的に速度を変える。speed とは排他 (ランプがあれば speed は無視する)。
  {
    key: 'speedRamp',
    owner: 'timelineItem',
    description:
      'シーンの中で速度をだんだん変える。UI は from / to / easing と解除だけを触れる (ランプ中は音声なし)',
    // 一定速度 (speed) を UI / ops で指定したらランプは捨てる。フリーズとも同時に持てない。
    conflictsWith: ['speed', 'freeze'],
    dependsOn: [],
    // 分割点の速度を境目に、前半 / 後半とも linear のランプへ近似する。
    onSplit: 'split',
    onRangeOrSpeedChange: 'keep',
    // Premiere / FCPXML は平均速度の一定速へ降格して注記する (§4.1-L)。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeSpeedRamp(value),
  },
  // ── 余白の色 (シナリオの「静止画をそのまま使う」シーン) ──
  // 映像が contain で収まったときの上下 / 左右の余白を塗る '#RRGGBB'。区間・速度・分割とは無関係。
  {
    key: 'backgroundColor',
    owner: 'timelineItem',
    description:
      '映像の余白 (出力の比率と合わない静止画 / 動画の上下 / 左右) の色。UI はシーン編集の「余白の色」、省略 = 黒',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere / FCPXML には当て先が無い (単色レイヤーを敷く手はあるが、書き出しでは注記に留める)。
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeItemBackgroundColor(value),
  },
  // ── シーン音声のフェード (§4.3-E) ──
  // シーン音声の区間ごとの音量 (区間ミュート / 減音)。素材秒で持つので区間 / 速度の変更では維持し、
  // 分割は素材秒の分割点で切り分ける (audio/audioRegions.ts の splitAudioRegionsAtSource)。
  {
    key: 'audioRegions',
    owner: 'timelineItem',
    description: 'シーン音声 (現場音) の区間ごとの音量 (素材秒・0 = ミュート)。UI は波形の上で区間を置く',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'split',
    onRangeOrSpeedChange: 'keep',
    // Premiere は Audio Levels のキーフレーム、Final Cut は adjust-volume の keyframeAnimation で表せる。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeAudioRegions(value),
  },
  // シーンの**表示区間**の両端が起点。速度・区間を変えても「頭 n 秒 / 尻 n 秒」の意味は変わらない
  // ので維持し、分割ではそのままコピーする (前半の尻・後半の頭にも同じ秒が効く点は許容)。
  {
    key: 'audioFadeInSec',
    owner: 'timelineItem',
    description: 'シーン音声 (現場音) のフェードイン秒。UI にコントロールは無く AI 専用',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere は Audio Levels のキーフレーム、Final Cut は adjust-volume の fadeIn で表せる。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeFadeSecValue(value),
  },
  {
    key: 'audioFadeOutSec',
    owner: 'timelineItem',
    description: 'シーン音声 (現場音) のフェードアウト秒。UI にコントロールは無く AI 専用',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'approximate',
    normalize: (value) => normalizeFadeSecValue(value),
  },
  // ── ナレーション 1 本ごとの音量 / フェード (§4.3-E) ──
  // UI にあるのは音量スライダーだけ (フェード秒は AI 専用)。ミュート (muted) とは直交する
  // (ミュートを解除すれば音量の値が戻る。シーン音声の volume / muted と同じ作法)。
  {
    key: 'volume',
    owner: 'narrationCue',
    description: 'ナレーション 1 本の音量 (0〜1)。UI はインスペクタのスライダー',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere の Audio Levels / Final Cut の adjust-volume に反映する。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeNarrationVolumeValue(value),
  },
  {
    key: 'fadeInSec',
    owner: 'narrationCue',
    description: 'ナレーション 1 本のフェードイン秒 (AI 専用)',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'approximate',
    normalize: (value) => normalizeFadeSecValue(value),
  },
  {
    key: 'fadeOutSec',
    owner: 'narrationCue',
    description: 'ナレーション 1 本のフェードアウト秒 (AI 専用)',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'approximate',
    normalize: (value) => normalizeFadeSecValue(value),
  },
  {
    key: 'durationSec',
    owner: 'narrationCue',
    description: 'ナレーション 1 本を鳴らす長さ (音源の頭から。途中で切るときだけ持つ)。UI はインスペクタの終了秒',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere / Final Cut ともクリップの out 点で表せる
    onXmlExport: 'approximate',
    normalize: (value) => normalizeNarrationPlayDurationValue(value),
  },
  {
    key: 'sourceStartSec',
    owner: 'narrationCue',
    description: 'ナレーション 1 本の入り点 (音源の頭からの秒。頭を切り詰めるときだけ持つ)。UI はインスペクタの波形トリムの左のつまみ',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere / Final Cut ともクリップの in 点で表せる
    onXmlExport: 'approximate',
    normalize: (value) => normalizeNarrationSourceStartValue(value),
  },
  // ── ナレーション中の BGM 自動ダッキング (§4.3-E) ──
  // プロジェクト単位の設定なので、シーン / テロップの分割・区間変更とは無関係。
  // 引き継ぎは汎用の carry ではなく look と同じ三値マージ (キー省略 = 維持 / null = 無効化 /
  // オブジェクト = 差し替え) で行う (proposal/itemSettingsContinuity.ts の mergeProposalDucking)。
  {
    key: 'ducking',
    owner: 'project',
    description:
      'ナレーション中に BGM を自動で下げる設定。UI はスイッチと強さの 3 択で、attack / release / hold は AI 専用',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere は Audio Levels のキーフレームに焼く。Final Cut は区間の途中に音量キーフレームを
    // 置けないので落として注記する (§0-2)。表に 1 つしか書けないので「近似」側を採る。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeDucking(value),
  },
  // ── 書き出し時のラウドネス正規化 (§4.3-M) ──
  // プロジェクト単位の設定で、**MP4 書き出しの後処理でだけ**掛かる (プレビューには効かない)。
  // 引き継ぎは汎用の carry ではなく ducking / look と同じ三値マージ
  // (proposal/itemSettingsContinuity.ts の mergeProposalLoudness)。
  {
    key: 'loudness',
    owner: 'project',
    description:
      '書き出す MP4 全体の音量を目標 LUFS へ揃える。UI はスイッチと目標の 3 択で、truePeakDb / lra は AI 専用',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere / FCPXML には対応物が無いので注記だけ (§0-2)。
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeLoudness(value),
  },
  // ── 全編に効くオーバーレイ (§4.1-F) ──
  // ロゴのウォーターマーク・バッジ。区間は**動画全体の絶対秒**で、省略 = 全編。
  // 引き継ぎは汎用の carry ではなく look / ducking と同じ三値マージ (キー省略 = 維持 /
  // `[]` = 全解除 / 配列 = 置換) で行う (proposal/itemSettingsContinuity.ts の mergeProjectOverlays)。
  {
    key: 'overlays',
    owner: 'project',
    description: '動画全体に重ねるオーバーレイ (ロゴ・バッジ・ぼかし)。UI は一覧と位置のプリセットだけ',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'approximate',
    normalize: (value) => {
      const { overlays } = normalizeOverlays(value);
      return overlays.length > 0 ? overlays : undefined;
    },
  },
  // ── テロップ (CaptionDesign) の拡張属性 (§4.2-B1 / §4.2-C) ──
  // 位置補正は「列挙 (position) + 補正」で任意位置を表す設計なので、UI で列挙を変えたら捨てる
  // (§0-3 の競合規則)。行間・アニメーションは列挙と直交するので常に保持する。
  // header / footer では補正が効かない (design 全体を見る normalizeCaptionExtensions が落とす)。
  {
    key: 'offsetXPercent',
    owner: 'captionDesign',
    description: 'テロップの横位置の補正 (出力幅に対する %。正 = 右)。UI は列挙の位置だけを選べる',
    conflictsWith: ['position'],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere は Text ジェネレータの Origin、Final Cut は title の Position で表せる。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeCaptionOffsetPercentValue(value),
  },
  {
    key: 'offsetYPercent',
    owner: 'captionDesign',
    description: 'テロップの縦位置の補正 (出力高さに対する %。正 = 下)。列挙の基準線からのずらし幅',
    conflictsWith: ['position'],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'approximate',
    normalize: (value) => normalizeCaptionOffsetPercentValue(value),
  },
  {
    key: 'lineHeight',
    owner: 'captionDesign',
    description: 'テロップの行間 (倍率。省略 = 箱 1.7 / それ以外 1.3。縁取り・部分強調ぶんの下限まで切り上がる)',
    // 位置の列挙とは独立 (position を変えても行間は保つ)。
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // ブロックの位置計算には効くが、Premiere / Final Cut の行送り自体は既定のままなので注記する。
    onXmlExport: 'omit-with-note',
    // 値だけの丸め (0.8〜2.5・0.01 刻み)。**下限 (縁取り・部分強調ぶん) は design 全体
    // (フォントサイズ・縁取り・強調) が要るので、ここではなく normalizeCaptionExtensions が掛ける**
    normalize: (value) => normalizeCaptionLineHeightValue(value),
  },
  {
    key: 'rotationDeg',
    owner: 'captionDesign',
    description: 'テロップの回転 (度。正 = 時計回り・±30・0.5 刻み)。帯 (band) と header / footer では落ちる',
    // 位置の列挙とは独立 (position を変えても傾きは保つ)。傾きと同じ意味の UI 項目は無い。
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere は Basic Motion の Rotation、Final Cut は adjust-transform の rotation で出せる
    // (回転の中心はブロック中心の近似)。
    onXmlExport: 'approximate',
    normalize: (value) => normalizeCaptionRotationValue(value),
  },
  {
    key: 'animationIn',
    owner: 'captionDesign',
    description: 'テロップの入場アニメーション (fade / slide-up / slide-down / pop / typewriter)',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // Premiere / FCPXML には対応物が無いので落として注記する (§0-2)。
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeAnimationKind(value, 'in'),
  },
  {
    key: 'animationOut',
    owner: 'captionDesign',
    description: 'テロップの退場アニメーション (fade / slide-down / pop)',
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeAnimationKind(value, 'out'),
  },
  {
    key: 'animationInSec',
    owner: 'captionDesign',
    description: '入場アニメーションの秒数 (0.1〜1.0。省略 = 0.3)',
    // 種類が無ければ意味を持たない (design 全体を見る normalizeCaptionExtensions が落とす)。
    conflictsWith: [],
    dependsOn: ['animationIn'],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeAnimationSec(value, 'in'),
  },
  {
    key: 'animationOutSec',
    owner: 'captionDesign',
    description: '退場アニメーションの秒数 (0.1〜1.0。省略 = 0.2)',
    conflictsWith: [],
    dependsOn: ['animationOut'],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeAnimationSec(value, 'out'),
  },
  // ── テロップ内の部分強調 (§4.2-I) ──
  // 「どこを」(範囲) は Caption 本体、「どう」(色・サイズ・太さ) は CaptionDesign に持つ。
  {
    key: 'emphasisRanges',
    owner: 'caption',
    description:
      'テロップ内の部分強調の範囲 (text 内のコードポイント単位の半開区間)。AI / UI は text の `**強調**` 記法で書く',
    // 位置や区間と同じ意味の値を持つ UI 項目は無い。
    conflictsWith: [],
    // **text の文字オフセットなので、text が変わったら破棄する** (§0-3 の依存規則)。
    // UI の文言エディタと AI の全体提案は text を記法付きで往復するので、実際に消えるのは
    // 「記法の無い text を明示的に差し替えた」ときだけ。
    dependsOn: ['text'],
    // 分割 (split_item) はテロップを表示区間で振り分けるだけで text は変えないのでそのまま。
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // .prtextstyle / FCPXML の title はテロップ単位のスタイルしか持てないので落として注記する。
    onXmlExport: 'omit-with-note',
    normalize: (value) => {
      // ここでは text の長さが分からないので、区間の形 (順序・重なり) だけを整える。
      // text 長でのクランプは schema/project.ts の normalizeCaptionEmphasisRanges が行う。
      const ranges = normalizeEmphasisRanges(value, Number.MAX_SAFE_INTEGER);
      return ranges && ranges.length > 0 ? ranges : undefined;
    },
  },
  {
    key: 'emphasis',
    owner: 'captionDesign',
    description: 'テロップ内の部分強調のスタイル (色 / サイズ倍率 / 太字)。範囲は Caption.emphasisRanges',
    // 見た目の指定なので位置・背景とは直交する。
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeCaptionEmphasisStyle(value),
  },
  // ── 単語ハイライト (カラオケ字幕。§4.2-B2) ──
  // 「どう光らせるか」(色 / 太字 / 先行秒) は CaptionDesign、「いつ・どこまで」(対応づけ) は
  // Caption 本体。design 側は**キーがあること自体が「使う」宣言**なので、空 `{}` でも保持する。
  {
    key: 'wordHighlight',
    owner: 'captionDesign',
    description:
      '単語ハイライト (カラオケ字幕) の見た目と有効化 (キーがあれば有効)。対応づけは Caption.wordTimings',
    // 見た目の指定なので位置・背景とは直交する。
    conflictsWith: [],
    dependsOn: [],
    onSplit: 'copy',
    onRangeOrSpeedChange: 'keep',
    // .prtextstyle / FCPXML の title はテロップ単位のスタイルしか持てないので落として注記する。
    onXmlExport: 'omit-with-note',
    normalize: (value) => normalizeCaptionWordHighlight(value),
  },
  {
    key: 'wordTimings',
    owner: 'caption',
    description:
      '単語ハイライトの対応づけ ({textHash, units})。コードの同期工程だけが書く導出値で AI / UI は書かない',
    // 同じ意味の値を持つ UI 項目は無い。
    conflictsWith: [],
    // **text の文字オフセット**と**シーン表示秒**の両方に依存する。文言を変えれば別の文字を指し、
    // 表示区間を動かせば発話とずれるので、どちらが変わっても破棄して再同期を促す (§0-3)。
    dependsOn: ['text', 'startSec', 'endSec'],
    // 分割 (split_item) はテロップを表示区間で振り分ける。**前半に丸ごと残るテロップは表示秒の
    // 原点が変わらないので保持**し、**後半へ移るテロップと分割点をまたぐテロップは破棄**する
    // (後半は分割点が 0 秒になるため、単純コピーでは秒がずれる)。宣言としては「破棄あり」を表す
    // 'drop' にしてある (実装は edit_ops の _op_split_item。再同期すれば戻る)。
    onSplit: 'drop',
    // 区間 (set_item_range) / 速度 (set_item_speed) の変更も表示秒の意味を変えるので破棄する。
    onRangeOrSpeedChange: 'drop',
    onXmlExport: 'omit-with-note',
    normalize: (value) => {
      // ここでは text が分からないのでハッシュ照合はできない。形 (キーの有無・配列) だけを見て、
      // text 長でのクランプとハッシュ照合は schema/project.ts の normalizeCaptionWordTimings が行う。
      if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
      const raw = value as { textHash?: unknown; units?: unknown };
      if (typeof raw.textHash !== 'string' || !Array.isArray(raw.units) || raw.units.length === 0) {
        return undefined;
      }
      return value;
    },
  },
];

/**
 * 位置補正 (%): ±CAPTION_OFFSET_LIMIT_PERCENT にクランプ → 0.1 刻みに丸め → **丸めた結果が 0 なら**
 * キーごと落とす (0.04 を「補正あり」として保存しないため。丸める前に 0 判定すると
 * 0.04 が offsetYPercent: 0 として残る)。
 *
 * 画面外に出さないための「位置ごとの上限」は design 全体 (position) を見ないと決まらないので、
 * ここではなく schema/project.ts の resolveCaptionOffsetPercent が持つ。
 */
export function normalizeCaptionOffsetPercentValue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const clamped = Math.min(50, Math.max(-50, value));
  const rounded = Math.floor(clamped * 10 + 0.5) / 10;
  return rounded === 0 ? undefined : rounded;
}

/**
 * フェード秒 (ナレーション / シーン音声。§4.3-E): 0〜MAX_FADE_SEC にクランプして 0.01 刻みに丸め、
 * **丸めた結果が 0 ならキーごと落とす** (0 秒のフェードを「設定あり」として保存しない)。
 * 区間長に対する比例縮小は評価時 (audio/audioCue.ts の clampFadePair) が行う。
 */
export const MAX_AUDIO_FADE_SEC = 10;

export function normalizeFadeSecValue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const rounded = Math.floor(Math.min(MAX_AUDIO_FADE_SEC, Math.max(0, value)) * 100 + 0.5) / 100;
  return rounded === 0 ? undefined : rounded;
}

/**
 * ナレーションの音量 (§4.3-E): 0〜1 にクランプして 0.01 刻みに丸め、**等倍 (1) はキーごと落とす**
 * (シーン音声の volume と同じ作法で、既定値を JSON に残さない)。
 */
export function normalizeNarrationVolumeValue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const rounded = Math.floor(Math.min(1, Math.max(0, value)) * 100 + 0.5) / 100;
  return rounded >= 1 ? undefined : rounded;
}

/** ナレーションを鳴らす長さ: 正の有限値だけを 0.01 秒刻みで残す (0 以下・不正値はキーごと落とす = 終わりまで)。 */
export function normalizeNarrationPlayDurationValue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
  const rounded = Math.floor(value * 100 + 0.5) / 100;
  return rounded > 0 ? rounded : undefined;
}

/** ナレーションの入り点: 正の有限値だけを 0.01 秒刻みで残す (0 以下・不正値はキーごと落とす = 頭から)。 */
export function normalizeNarrationSourceStartValue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
  const rounded = Math.floor(value * 100 + 0.5) / 100;
  return rounded > 0 ? rounded : undefined;
}

/** 行間: 0.8〜2.5 にクランプして 0.01 刻みに丸める。既定値の解決 (箱 1.7) は project.ts 側。 */
export function normalizeCaptionLineHeightValue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.floor(Math.min(2.5, Math.max(0.8, value)) * 100 + 0.5) / 100;
}

// アニメーションの種類・秒数は schema/captionAnimation.ts の normalizeCaptionAnimation が正本なので、
// 宣言の normalize はそこへ委譲する (規則を 2 か所に書かない)。
function normalizeAnimationKind(value: unknown, direction: 'in' | 'out'): string | undefined {
  const normalized = normalizeCaptionAnimation(
    direction === 'in' ? { animationIn: value as string } : { animationOut: value as string },
  );
  return direction === 'in' ? normalized.animationIn : normalized.animationOut;
}

function normalizeAnimationSec(value: unknown, direction: 'in' | 'out'): number | undefined {
  // 種類が無いと秒数は落ちるので、値の丸めだけを見るために有効な種類を仮に置く
  const normalized = normalizeCaptionAnimation(
    direction === 'in'
      ? { animationIn: 'fade', animationInSec: value as number }
      : { animationOut: 'fade', animationOutSec: value as number },
  );
  return direction === 'in' ? normalized.animationInSec : normalized.animationOutSec;
}

/** owner ごとの拡張属性の宣言。引き継ぎ処理 (itemSettingsContinuity) が走査する。 */
export function extensionAttributesFor(owner: ExtensionAttributeOwner): ExtensionAttributeDeclaration[] {
  return EXTENSION_ATTRIBUTES.filter((a) => a.owner === owner);
}

/** owner ごとの拡張属性キー。UI / 引き継ぎ処理が「落としてはいけないキー」を参照するのに使う。 */
export function extensionAttributeKeys(owner: ExtensionAttributeOwner): string[] {
  return extensionAttributesFor(owner).map((a) => a.key);
}

/**
 * 指定の属性を UI で変更したときにリセットすべき拡張属性キー (競合宣言の逆引き)。
 *
 * **`dependsOn` はここには含めない**。`conflictsWith` は「同じ意味の値を UI で変えたら補正は
 * 意味を失う」という即時のリセットだが、`dependsOn` は属性ごとに扱いが違う
 * (`animationInSec` は種類が無ければ正規化で落ちるだけ、`emphasisRanges` は text が変わったら
 * 破棄する) ため、宣言を読む側 (captionContinuity の carryCaptionExtensions) が個別に解釈する。
 */
export function conflictingExtensionAttributes(owner: ExtensionAttributeOwner, changedKey: string): string[] {
  return EXTENSION_ATTRIBUTES.filter((a) => a.owner === owner && a.conflictsWith.includes(changedKey)).map(
    (a) => a.key,
  );
}

/**
 * UI のフォームが部分更新 (patch) を当てるときの共通処理 (§0-3)。
 * 1. `{...current, ...patch}` で UI が知らない属性をそのまま保持する
 * 2. patch が「同じ意味の値」を持つ属性 (競合宣言 conflictsWith) を**別の値に**変えたときだけ、
 *    競合する拡張属性を削除する (例: テロップの position を選び直したら offsetX / offsetY を捨てる)
 *
 * 競合判定は宣言 (EXTENSION_ATTRIBUTES) を引くだけで、属性名を各フォームに散らさない。
 */
export function applyExtensionConflicts<T extends Record<string, unknown>>(
  owner: ExtensionAttributeOwner,
  current: T,
  patch: Partial<T>,
): T {
  const next: Record<string, unknown> = { ...current, ...patch };
  for (const changedKey of Object.keys(patch)) {
    if (patch[changedKey as keyof T] === current[changedKey as keyof T]) continue;
    for (const key of conflictingExtensionAttributes(owner, changedKey)) {
      // patch 自身がその拡張属性を明示しているなら、そちらの意図を優先する
      if (key in patch) continue;
      delete next[key];
    }
  }
  return next as T;
}

/**
 * AI が別の属性を書き換えた (= オブジェクトを作り直した) ときの拡張属性の引き継ぎ (§0-3)。
 * `next` にキーが無ければ「変更なし」とみなして `source` の値を引き継ぎ、競合する属性
 * (conflictsWith) が `source` と違う値になっていたらその拡張属性は引き継がない (リセット)。
 * 依存先 (dependsOn) の扱いは属性ごとに違うので、この汎用の引き継ぎでは見ない
 * (conflictingExtensionAttributes のコメント参照)。
 */
export function carryExtensionAttributes<T extends Record<string, unknown>>(
  owner: ExtensionAttributeOwner,
  source: T | null | undefined,
  next: T,
): T {
  if (!source) return next;
  const result: Record<string, unknown> = { ...next };
  let carried = false;
  for (const attr of extensionAttributesFor(owner)) {
    if (attr.key in next) continue;
    const inherited = source[attr.key as keyof T];
    if (inherited === undefined || inherited === null) continue;
    // 競合する属性が変わっていたら、引き継がずに捨てる (UI の操作と同じ規則)
    const conflicted = attr.conflictsWith.some((key) => next[key as keyof T] !== source[key as keyof T]);
    if (conflicted) continue;
    result[attr.key] = inherited;
    carried = true;
  }
  // 何も引き継がなければ元のオブジェクトをそのまま返す (呼び出し側の「変更あり」判定を濁さない)
  return carried ? (result as T) : next;
}
